import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { API_ENDPOINTS } from "../../../../util/constants";
import { actionTypes } from "../../../Auth/_redux/authRedux";
import { actions } from "../../../Dashboard/_redux/dashboardRedux";

const initialListingsState = {
  queuesList: [],
  queuesMeta: {},
  currentPage: 1,
  pageSize: 10,
  soundsList: [],
  searchColumns: [],
  searchQuery: null,
  mappedAgentsList: [],
  mappedAgentsMeta: {},
  agentsList: [],
  agentsListMeta: {},
};

export const fetchSoundsList = createAsyncThunk(
  "queuesSlice/fetchSoundsList",
  async (params, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.get(API_ENDPOINTS.getSoundLists);
      return await response;
    } catch (error) {
      return rejectWithValue({ error: error.message });
    }
  }
);

export const fetchQueuesList = createAsyncThunk(
  "queuesSlice/fetchQueuesList",
  async (params, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.get(API_ENDPOINTS.getQueueDetails, {
        params: params,
      });
      dispatch(
        actions.setLoadingState({
          resource: "queue_lists",
          loading_state: "loaded",
        })
      );
      return await response;
    } catch (error) {
      dispatch(
        actions.setLoadingState({
          resource: "queue_lists",
          loading_state: "loaded",
        })
      );
      return rejectWithValue({ error: error.message });
    }
  }
);

export const queueDelete = createAsyncThunk(
  "queuesSlice/queueDelete",
  async (params, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.delete(
        API_ENDPOINTS.getQueueDetails + params + "/"
      );
      dispatch(
        actions.setLoadingState({
          resource: "queue_delete",
          loading_state: "loaded",
        })
      );
      return await response;
    } catch (error) {
      dispatch(
        actions.setLoadingState({
          resource: "queue_delete",
          loading_state: "loaded",
        })
      );
      return rejectWithValue(error);
    }
  }
);

export const queueCreation = createAsyncThunk(
  "queuesSlice/queueCreation",
  async (params, { dispatch, rejectWithValue }) => {
    const { q_uuid } = params;
    try {
      const response = await axios({
        url: API_ENDPOINTS.getQueueDetails + (q_uuid === "new" ? "" : q_uuid),
        method: q_uuid === "new" ? "post" : "put",
        data: params,
      });
      dispatch(
        actions.setLoadingState({
          resource: "queue_upsert",
          loading_state: "loaded",
        })
      );
      return await response;
    } catch (error) {
      dispatch(
        actions.setLoadingState({
          resource: "queue_upsert",
          loading_state: "loaded",
        })
      );
      return rejectWithValue({ error: error?.response });
    }
  }
);

export const fetchAgentsList = createAsyncThunk(
  "queuesSlice/fetchAgentsList",
  async (params, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.get(API_ENDPOINTS.getTotalAgentsList, {
        params: params,
      });
      dispatch(
        actions.setLoadingState({
          resource: "total_agents",
          loading_state: "loaded",
        })
      );
      return await response;
    } catch (error) {
      dispatch(
        actions.setLoadingState({
          resource: "total_agents",
          loading_state: "loaded",
        })
      );
      return rejectWithValue({ error: error?.response });
    }
  }
);

export const fetchMappedToQueueAgentsList = createAsyncThunk(
  "queuesSlice/fetchMappedToQueueAgentsList",
  async (params, { dispatch, rejectWithValue }) => {
    const { queue__q_uuid } = params;
    try {
      const response = await axios.get(API_ENDPOINTS.agentsMappingToQueue, {
        params: { queue__q_uuid },
      });
      dispatch(
        actions.setLoadingState({
          resource: "agents_mapping",
          loading_state: "loaded",
        })
      );
      return await response;
    } catch (error) {
      dispatch(
        actions.setLoadingState({
          resource: "agents_mapping",
          loading_state: "loaded",
        })
      );
      return rejectWithValue({ error: error?.response });
    }
  }
);

export const mapAgentsToQueue = createAsyncThunk(
  "queuesSlice/mapAgentsToQueue",
  async (params, { dispatch, rejectWithValue }) => {
    const { queue__q_uuid, method, ...rest } = params;
    try {
      const response = await axios({
        url: API_ENDPOINTS.agentsMappingToQueue,
        method: method,
        params: method === "get" ? { queue__q_uuid } : "",
        ...(method !== "get" && { data: rest }),
      });
      dispatch(
        actions.setLoadingState({
          resource: "agents_mapping",
          loading_state: "loaded",
        })
      );
      return await response;
    } catch (error) {
      dispatch(
        actions.setLoadingState({
          resource: "agents_mapping",
          loading_state: "loaded",
        })
      );
      return rejectWithValue({ error: error?.response });
    }
  }
);

export const queuesSlice = createSlice({
  name: "queuesSlice",
  initialState: { ...initialListingsState },

  extraReducers: (builder) => {
    builder.addCase(actionTypes.Logout, (state, action) => {
      return { ...initialListingsState };
    });

    builder.addCase(fetchSoundsList.pending, (state) => {
      state.soundsList = [];
    });
    builder.addCase(fetchSoundsList.fulfilled, (state, { payload }) => {
      state.soundsList = payload?.data?.objects;
    });
    builder.addCase(fetchSoundsList.rejected, (state, action) => {});

    builder.addCase(fetchQueuesList.pending, (state) => {
      state.queuesList = [];
    });
    builder.addCase(fetchQueuesList.fulfilled, (state, { payload }) => {
      state.queuesList = payload?.data?.objects;
      state.queuesMeta = payload?.data?.meta;
    });
    builder.addCase(fetchQueuesList.rejected, (state, action) => {
      state.queuesMeta ={}
    });

    builder.addCase(fetchMappedToQueueAgentsList.pending, (state) => {
      state.mappedAgentsList = [];
    });
    builder.addCase(
      fetchMappedToQueueAgentsList.fulfilled,
      (state, { payload }) => {
        state.mappedAgentsList = payload?.data?.objects;
        state.mappedAgentsMeta = payload?.data?.meta;
      }
    );
    builder.addCase(
      fetchMappedToQueueAgentsList.rejected,
      (state, action) => {}
    );

    builder.addCase(fetchAgentsList.pending, (state) => {
      state.agentsList = [];
    });
    builder.addCase(fetchAgentsList.fulfilled, (state, { payload }) => {
      state.agentsList = payload?.data?.objects;
      state.agentsListMeta = payload?.data?.meta;
    });
    builder.addCase(fetchAgentsList.rejected, (state, action) => {});
  },

  reducers: {
    setQueuesTableConfigureColumns: (state, action) => {
      state.searchColumns = action.payload;
    },
    setQueueSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    onPageAndPageSizeChange: (state, action) => {
      const { payload } = action;
      state.currentPage = payload["currentPage"];
      state.pageSize = payload["pageSize"];
    },
  },
});

export const {
  setQueuesTableConfigureColumns,
  onPageAndPageSizeChange,
  setQueueSearchQuery,
} = queuesSlice.actions;

export default queuesSlice.reducer;
